import * as React from 'react';

import { CircularProgress } from '@mui/material';
import { loginByEmailCode } from '@realadvisor/auth-client';
import { Flex } from 'react-system';

import { useLocale } from '@legacy/hooks/locale';
import { useTheme } from '@legacy/hooks/theme';
import { describeAuthError } from '@legacy/routes/Login/authErrors';
import { getAfterLoginHref } from '@legacy/routes/Login/loginRedirects';
import { PinInput } from '@legacy/routes/Login/PinInput';

// Emailed codes are 5 digits — cloudflare-workers/verify.ts, generateCode(5).
const EMAIL_CODE_LENGTH = 5;

type Props = {
  email: string;
  tenantId: string | null;
};

type State = {
  status: 'idle' | 'verifying' | 'error';
  error: string | null;
  // Bumping this remounts PinInput so the boxes clear and refocus.
  attempt: number;
};

export const LoginEmailVerifyCodeForm = ({ email, tenantId }: Props) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const [{ status, error, attempt }, setState] = React.useState<State>({
    status: 'idle',
    error: null,
    attempt: 0,
  });

  const submit = (code: string) => {
    setState(prev => ({ ...prev, status: 'verifying', error: null }));
    loginByEmailCode(email, { code, tenantId })
      .then(async response => {
        if (response.ok) {
          location.href = getAfterLoginHref(tenantId);
          return;
        }
        // 401 is the expected wrong/expired/burnt code. Everything else is a
        // machine token, mapped rather than shown.
        const detail = await response
          .json()
          .then(body => body.error)
          .catch(() => null);
        setState(prev => ({
          status: 'error',
          error:
            response.status === 401
              ? t('The code is invalid or has expired.')
              : describeAuthError(detail, t),
          attempt: prev.attempt + 1,
        }));
      })
      .catch(err => {
        setState(prev => ({
          status: 'error',
          error: describeAuthError(
            err instanceof Error ? err.message : null,
            t,
          ),
          attempt: prev.attempt + 1,
        }));
      });
  };

  return (
    <>
      <PinInput
        key={attempt}
        length={EMAIL_CODE_LENGTH}
        label={t('Enter the code we emailed you')}
        disabled={status === 'verifying'}
        error={status === 'error'}
        onChange={submit}
      />

      {/* Fixed height so the card doesn't jump between the three states. */}
      <Flex
        justifyContent="center"
        alignItems="center"
        css={{ minHeight: 28, marginTop: 12, textAlign: 'center' }}
      >
        {status === 'verifying' && (
          <>
            <CircularProgress size={14} css={{ marginRight: 8 }} />
            <span css={{ fontSize: 13, color: colors.mediumText }}>
              {t('Verifying…')}
            </span>
          </>
        )}
        {status === 'error' && error != null && (
          <span css={{ fontSize: 13, color: colors.errorText }}>{error}</span>
        )}
      </Flex>
    </>
  );
};
